import { createTheme, adaptV4Theme } from '@mui/material/styles';

export const palette = {
    primary: {
        50: '#EFFEF8',
        100: '#DAFEEF',
        200: '#B7FBE0',
        300: '#80F5C7',
        400: '#41E7A7',
        500: '#18CF88',
        600: '#0EAB6F',
        700: '#0F8658',
        800: '#116A49',
        900: '#10573D',
        main: '#033826'
    },
    secondary: {
        50: '#FFF5ED',
        100: '#FFE9D4',
        200: '#FFCFA8',
        300: '#FFAC70',
        400: '#FF7E37',
        main: '#FF6924',
        600: '#F04006',
        700: '#C72D07',
        800: '#9E240E',
        900: '#9E240E',
        950: '#9E240E'
    },
    tertiary: {
        50: '#FDF2F9',
        100: '#FCE7F4',
        200: '#FBCFEA',
        300: '#FAA7D8',
        400: '#F571BD',
        500: '#ED47A1',
        600: '#D12178',
        700: '#C01665',
        800: '#9F1555',
        900: '#841748',
        main: '#510628'
    },
    success: {
        50: '#F3FAF3',
        100: '#E3F5E3',
        200: '#C8EAC9',
        300: '#9CD99E',
        400: '#69BF6C',
        500: '#43A047',
        600: '#348538',
        700: '#2B6A2F',
        800: '#275429',
        900: '#214624',
        main: '#0E2510',
    },
    info: {
        50: '#ECF9FF',
        100: '#D5EFFF',
        200: '#B5E4FF',
        300: '#82D5FF',
        400: '#47BAFF',
        500: '#1D99FF',
        600: '#0578FF',
        700: '#0061F6',
        800: '#074DC6',
        900: '#0D47A1',
        main: '#0D2B5E',
    },
    error: {
        50: '#fef3f2',
        100: '#FFE3E1',
        200: '#FFCBC8',
        300: '#FFA7A2',
        400: '#FD746C',
        500: '#F6473D',
        600: '#E32A1F',
        main: '#B71E15',
        800: '#9E1E16',
        900: '#831F19',
        950: '#470C08',
    },
    disabled: {
        50: '#f6f6f6',
        100: '#e7e7e7',
        200: '#d1d1d1',
        300: '#b0b0b0',
        main: '#8D8D8D',
        500: '#6d6d6d',
        600: '#5d5d5d',
        700: '#4f4f4f',
        800: '#454545',
        900: '#3d3d3d',
        950: '#262626',
    },
    background: {
        default: '#FAF5F2',
    },
    diamond: {
        50: '#F0FDFB',
        100: '#CDFAF2',
        200: '#9BF4E7',
        300: '#61E7D8',
        400: '#31D0C3',
        500: '#1AC7BC',
        600: '#10918C',
        700: '#117471',
        800: '#135C5B',
        900: '#144D4C',
        main: '#052D2E',
    },
    sapphire: {
        50: '#EEF9FF',
        100: '#D9F0FF',
        200: '#BCE6FF',
        300: '#8ED8FF',
        400: '#59BFFF',
        500: '#33A1FE',
        600: '#1C84F4',
        700: '#156BE0',
        800: '#1A5FC7',
        900: '#194B8F',
        main: '#142F57',
    },
    golden: {
        50: '#FEFDE8',
        100: '#FEFCC3',
        200: '#FEF58A',
        300: '#FCE848',
        400: '#F9D616',
        500: '#E9BD09',
        600: '#DB9F05',
        700: '#A16907',
        800: '#85520E',
        900: '#714312',
        main: '#422306',
    },
    silver: {
        50: '#F8F8F8',
        100: '#F1EFF1',
        200: '#E5E3E4',
        300: '#CAC6C9',
        400: '#B7B2B6',
        500: '#9D969B',
        600: '#857D83',
        700: '#6E676C',
        800: '#5C585B',
        900: '#504C4F',
        main: '#292628',
    },
    bronze: {
        50: '#F7F5EF',
        100: '#EBE6D6',
        200: '#DACCAE',
        300: '#C4AC80',
        400: '#B2915D',
        500: '#A37F4F',
        600: '#8C6642',
        700: '#714E37',
        800: '#604333',
        900: '#533B30',
        main: '#2F1F19',
    },
    yellow: {
        50: '#FEF9EC',
        100: '#FCEDC9',
        200: '#F9D98E',
        300: '#F6C053',
        400: '#F4AB34',
        500: '#ED8613',
        600: '#D2620D',
        700: '#AE440F',
        800: '#8D3513',
        900: '#742C13',
        main: '#431505',
    },
    beige: {
        50: '#FAF5F2',
        100: '#F3EAE1',
        200: '#E0C8B4',
        300: '#D6B49B',
        400: '#C49073',
        500: '#B87757',
        600: '#AA644C',
        700: '#8E5140',
        800: '#734339',
        900: '#5E3830',
        main: '#321C18'
    },
    icons: '#4F7467',
    white: '#FFFFFF',
    cardShadow: '0rem 0rem 2.308rem 0rem rgba(82, 63, 105, 0.06)',
    modalShadow: '0rem 0.923rem 1.846rem rgba(0, 0, 0, 0.08)',
};

const customTheme = {
    palette: palette,
    typography: {
        fontFamily: ['Roboto', 'Inter'].join(','),
        pageHeaderTitle: {
            fontSize: '20px',
            fontWeight: 600,
            lineHeight: '24px',
            letterSpacing: '0.24px'
        },
        widgetLink: {
            fontSize: '12px',
            fontWeight: '400',
            fontStile: 'normal',
            lineHeight: '20px',
            letterSpacing: '0.4px'
        },
        pageTitle: {
            fontSize: '18px',
            fontWeight: 600,
            lineHeight: '24px',
            letterSpacing: '0.216px',
            color: palette.disabled[950]
        },
        button: {
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '20px',
        },
        table: {
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '20px',
        },
        menuLabel: {
            fontSize: '10px',
            fontWeight: 500,
            lineHeight: '16px',
        },
        subMenuTitle: {
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '24px',
        },
        filterTitle: {
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '0.4px',
            color: palette.disabled[950]
        },
        sessionClose: {
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '36px',
        },
        body1: { color: '#151515', fontSize: '12px', fontWeight: '400', lineHeight: 'px', letterSpacing: '0.25px' },
        body2: {
            fontWeight: '500',
            fontSize: '13px',
        },
        subtitle1: {
            fontWeight: '700',
            fontSize: '13px',
        },
        h6: {
            fontWeight: '600',
            fontSize: '18px',
        },
        subtitle2: {
            fontWeight: '400',
            fontSize: '14px',
            color: palette.primary.main
        },
        h1: { // munu dialog
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '24px',
        },
        h2: { // action dialog tittle
            fontSize: '24px',
            fontWeight: 500,
            lineHeight: '32px',
        },
        h3: { // client data tittle
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: 'normal',
            color: '#8D8D8D'
        },
        h4: { // client data content
            fontSize: '16px',
            fontWeight: 700,
            lineHeight: 'normal',
            color: palette.disabled[950]
        },
        h5: { // client data content & header tittle
            fontSize: '18px',
            fontWeight: 600,
            color: palette.disabled[950]
        }
    }
};

export default createTheme(adaptV4Theme(customTheme));
